import { useLocale } from "@calcom/lib/hooks/useLocale";
import { trpc } from "@calcom/trpc";
import { showToast, Button } from "@calcom/ui";
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from "@components/auth/OtpField";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { signIn } from "next-auth/react";
import { useEffect, useState } from "react";

export function OtpForm({ email, name, onSuccess }: { email: string; name?: string; onSuccess: () => void }) {
  const { t, i18n } = useLocale();

  const [otp, setOtp] = useState("");

  const [loading, setLoading] = useState(false);

  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);

      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const sendOtp = trpc.viewer.auth.sendOtp.useMutation({
    onSuccess: () => {
      setCountdown(30);

      setLoading(false);

      showToast(t("verification_code_sent"), "success");
    },

    onError: (err) => {
      setLoading(false);

      if (err.data?.code === "TOO_MANY_REQUESTS") {
        showToast(t("too_many_requests"), "error");
      } else {
        showToast(t("failed_to_send_code"), "error");
      }
    },
  });

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);
    const trimmedName = name?.trim();

    const res = await signIn("otp", {
      email: email.toLowerCase().trim(),

      otpCode: otp.trim(),

      ...(trimmedName ? { name: trimmedName } : {}),

      redirect: false,
    });

    if (res?.error) {
      showToast(t("wrong_code"), "error");

      setLoading(false);
    } else {
      onSuccess();
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);

    sendOtp.mutate({ email: email.toLowerCase().trim(), language: i18n.language });
  };

  return (
    <>
      <form onSubmit={handleOtpSubmit}>
        <label
          className="mb-2 block text-sm font-medium leading-none text-gray-700"
          htmlFor="otp-verification">
          {t("otp_code")}
        </label>
        <InputOTP
          id="otp-verification"
          className="mx-auto"
          maxLength={6}
          pattern={REGEXP_ONLY_DIGITS}
          value={otp}
          onChange={(v) => setOtp(v)}>
          <InputOTPGroup>
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
          </InputOTPGroup>
          <InputOTPSeparator />
          <InputOTPGroup>
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTPGroup>
        </InputOTP>

        {/* <TextField
                            label={t("enter_6_digit_code")}
                            type="text"
                            required
                            autoFocus
                            maxLength={6}
                            placeholder="000000"
                            className="text-center font-mono text-xl tracking-[0.4em]" /> */}

        <Button type="submit" color="primary" className="mt-6 w-full justify-center" loading={loading}>
          {t("verify_and_login")}
        </Button>
      </form>

      <button
        type="button"
        disabled={countdown > 0 || loading}
        className="text-brand-500 my-4 w-full text-center text-sm font-medium hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
        onClick={handleResendOtp}>
        {countdown > 0 ? `${t("resend_code_in")} ${countdown}s` : t("resend_code")}
      </button>
    </>
  );
}
